import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItemButton,
  ListItemText,
  Chip,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Divider,
  Tooltip,
  Switch,
  FormControlLabel,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import api from '../../../services/api';

// Super-admin Email Settings page. Lets us edit the subject and (where
// supported) the HTML body for each transactional email type. Changes are
// persisted in the email_templates table and picked up by the email service
// on the next send.
const EmailSettingsPage = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/admin/email-templates');
      setTemplates(res.data);
      if (res.data.length && !selectedKey) {
        setSelectedKey(res.data[0].template_key);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load email templates');
    } finally {
      setLoading(false);
    }
  }, [selectedKey]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.template_key === selectedKey) || null,
    [templates, selectedKey]
  );

  // Reset the draft whenever the user picks a different template or the list
  // refetches; keeping the draft separate from the list lets us discard
  // unsaved edits without losing the ability to refresh.
  useEffect(() => {
    if (selectedTemplate) {
      setDraft({
        subject: selectedTemplate.subject || '',
        body_html: selectedTemplate.body_html || '',
        is_enabled: selectedTemplate.is_enabled !== false,
      });
      setSaveMessage(null);
    } else {
      setDraft(null);
    }
  }, [selectedTemplate]);

  const isDirty = useMemo(() => {
    if (!selectedTemplate || !draft) return false;
    return (
      (selectedTemplate.subject || '') !== draft.subject ||
      (selectedTemplate.body_html || '') !== draft.body_html ||
      (selectedTemplate.is_enabled !== false) !== draft.is_enabled
    );
  }, [selectedTemplate, draft]);

  const handleSave = async () => {
    if (!selectedTemplate || !draft) return;
    setSaving(true);
    setSaveMessage(null);
    try {
      const res = await api.put(`/admin/email-templates/${selectedTemplate.template_key}`, {
        subject: draft.subject || null,
        body_html: draft.body_html || null,
        is_enabled: draft.is_enabled,
      });
      setTemplates((prev) =>
        prev.map((t) => (t.template_key === res.data.template_key ? res.data : t))
      );
      setSaveMessage({ type: 'success', text: 'Template saved.' });
    } catch (err) {
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to save template',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!selectedTemplate) return;
    setDraft({
      subject: selectedTemplate.subject || '',
      body_html: selectedTemplate.body_html || '',
      is_enabled: selectedTemplate.is_enabled !== false,
    });
    setSaveMessage(null);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h5">Email Settings</Typography>
          <Typography variant="body2" color="text.secondary">
            Edit the subject and HTML body sent for each transactional email. Variables in
            the form <code>{'{{name}}'}</code> are replaced before sending.
          </Typography>
        </Box>
        <Tooltip title="Reload templates">
          <span>
            <Button
              startIcon={<RefreshIcon />}
              onClick={fetchTemplates}
              disabled={loading}
              variant="outlined"
            >
              Refresh
            </Button>
          </span>
        </Tooltip>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '320px 1fr' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ minHeight: 320 }}>
          {loading && !templates.length ? (
            <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List disablePadding>
              {templates.map((t) => {
                const overridden = Boolean(t.subject || t.body_html);
                return (
                  <ListItemButton
                    key={t.template_key}
                    selected={selectedKey === t.template_key}
                    onClick={() => setSelectedKey(t.template_key)}
                    sx={{ alignItems: 'flex-start' }}
                  >
                    <ListItemText
                      primary={t.name}
                      secondary={t.template_key}
                      primaryTypographyProps={{ fontWeight: 600 }}
                    />
                    <Stack direction="column" spacing={0.5} alignItems="flex-end">
                      {overridden && (
                        <Chip size="small" color="primary" label="Customized" />
                      )}
                      {t.is_enabled === false && (
                        <Chip size="small" color="warning" label="Disabled" />
                      )}
                    </Stack>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          {!selectedTemplate || !draft ? (
            <Typography color="text.secondary">Select a template to edit.</Typography>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">{selectedTemplate.name}</Typography>
                {selectedTemplate.description && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedTemplate.description}
                  </Typography>
                )}
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={draft.is_enabled}
                    onChange={(e) =>
                      setDraft((prev) => ({ ...prev, is_enabled: e.target.checked }))
                    }
                  />
                }
                label={draft.is_enabled ? 'Enabled — uses your overrides' : 'Disabled — falls back to defaults'}
              />

              <TextField
                label="Subject"
                value={draft.subject}
                onChange={(e) => setDraft((prev) => ({ ...prev, subject: e.target.value }))}
                helperText="Leave blank to use the built-in subject."
                fullWidth
              />

              <TextField
                label="HTML Body"
                value={draft.body_html}
                onChange={(e) => setDraft((prev) => ({ ...prev, body_html: e.target.value }))}
                helperText="Leave blank to use the built-in template file."
                fullWidth
                multiline
                minRows={14}
                InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
              />

              {Array.isArray(selectedTemplate.variables) && selectedTemplate.variables.length > 0 && (
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Available variables
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedTemplate.variables.map((v) => (
                      <Tooltip key={v.name} title={v.description || ''}>
                        <Chip size="small" label={`{{${v.name}}}`} variant="outlined" />
                      </Tooltip>
                    ))}
                  </Box>
                </Box>
              )}

              <Divider />

              {saveMessage && (
                <Alert severity={saveMessage.type}>{saveMessage.text}</Alert>
              )}

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  disabled={!isDirty || saving}
                  onClick={handleSave}
                >
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button onClick={handleReset} disabled={!isDirty || saving}>
                  Discard
                </Button>
              </Stack>
            </Stack>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default EmailSettingsPage;
